import React from 'react'
import { AutofitIFrame } from '@ta-interaktiv/react-autofit-iframe'
import { Link } from '@ta-interaktiv/contentbricks'
import { parseHostname } from '@ta-interaktiv/parse-hostname'

export const LOCALES_FR = {
  keycantons: ['GR', 'VD', 'GE'],
  key: [
    'moins bon, davantage de restrictions',
    'meilleur, plus ouvert',
    'Moyenne'
  ],
  nodata: 'pas disponible',
  credits: ['Analyse des données et texte', 'Graphiques et visualisation'],
  updated: 'Mis à jour le 11 juillet, 2021',
  questions: [
    'Ce contenu était-il informatif?',
    'La mise en forme vous plaît-elle?'
  ],
  showAllButton: ['Montrer tous les cantons', 'Moins de cantons'],
  authors:
    'Titus Plattner, Simone Luchetta, Patrick Meier et Sebastian Broschinski',
  kicker: 'Grand bilan de la pandémie'
}

export const TEXT_FR = {
  title: <>Quels cantons ont le mieux géré la crise du Covid?</>,
  lead: (
    <>
      Grâce à une approche offensive, les Grisons et Bâle sont les cantons qui
      ont le mieux traversé la deuxième vague de Covid-19. Comment les cantons
      romands s’en sont-ils sortis? Notre analyse.{' '}
    </>
  ),
  intro: {
    title: <></>,
    text: (
      <>
        <p>
          Fin juin, le Conseil fédéral a annoncé les dernières grandes étapes
          vers l’ouverture, les mesures sanitaires ont été fortement assouplies,
          l’obligation de home office et celle de porter un masque à l’extérieur
          ont été abandonnées, et on peut à nouveau s’asseoir en groupe dans les
          restaurants. Alors que l’on vit une sorte de retour à la normalité, il
          est temps de faire un premier bilan. Comment les cantons s’en sont-ils
          tirés durant la phase aiguë de la pandémie?
        </p>
        <p>
          Avec l’aide de scientifiques, nous avons choisi sept indicateurs
          permettant de comparer les cantons entre eux. Des restrictions
          sanitaires au taux de chômage en passant par la stratégie de test.
        </p>
      </>
    )
  },
  result1: {
    title: <>Le résultat</>,
    text: (
      <>
        <p>
          Le canton des Grisons et les deux Bâles ont obtenu les meilleurs
          résultats. Ils ont testé avec célérité et vacciné leurs populations
          dès le départ. En même temps, leur taux de positivité est resté
          faible, tout comme le nombre de morts.
        </p>
      </>
    )
  },
  result2: {
    title: <></>,
    text: (
      <>
        <p>
          Dans les cantons de Vaud et de Genève, comme dans le reste de la
          Suisse, l’économie a largement été laissée ouverte, avec une
          augmentation relativement faible du chômage (+1,1 à 1,2 point). C’est
          très peu en comparaison internationale, mais davantage que la moyenne
          helvétique. Les restrictions des deux cantons, elles, ont également
          été plus sévères que dans de nombreux autres cantons. Les deux bulles
          en haut à droite du graphique représentent ces deux indicateurs. Elles
          sont rouges pour Genève et Vaud, ce qui veut dire que ces derniers
          s’en sont moins bien tirés que la moyenne nationale.
        </p>
      </>
    )
  },
  result3: {
    title: <></>,
    text: (
      <>
        <p>
          Globalement, on voit que Genève, pour sa gestion de la pandémie, se
          situe dans le premier tiers, tandis que Vaud se place plutôt dans le
          dernier tiers. Les critères utilisés restent toutefois sujets à une
          pondération, que nous n’avons pas souhaité faire nous-mêmes: Vaud se
          distingue par exemple par une surmortalité moins élevée que la moyenne
          nationale durant la deuxième vague.
        </p>
        <p>
          Saint-Gall, Fribourg et le canton du Jura, qui ont aussi connu le plus
          de morts entre septembre 2020 et mai 2021, ferment clairement la
          marche.
        </p>
      </>
    )
  },
  excessmort: {
    title: <>1. La surmortalité</>,
    text: (
      <>
        <p>
          Durant la deuxième vague, alors que les autorités avaient toutes eu le
          temps de s’y préparer, le nombre de morts d’un canton à l’autre a
          fortement varié. Entre septembre 2020 et mai 2021, Saint-Gall et
          Fribourg ont connu l’augmentation de la mortalité la plus élevée, avec
          30% et 27% de morts en plus que les années normales chez les plus de
          65 ans. Comment expliquer ces différences? Les EMS ont-ils été moins
          bien protégés dans certains cantons que dans d’autres?
        </p>
        <AutofitIFrame
          className='AFI'
          src='https://datawrapper.dwcdn.net/zpOlQ/'
        />
        <p>
          «Il y a beaucoup de facteurs qui influencent le nombre de cas et
          finalement celui de morts», explique la professeure Olivia Keiser,
          cheffe de la division maladies infectieuses et modélisation
          mathématique à l’Institut de santé globale de l’Université de Genève.
          «Et on ne comprend toujours pas très bien l’impact exact de chacun
          d’eux, poursuit-elle. Le nombre de contacts, par exemple, est lui-même
          corrélé avec la densité de la population ou l’interconnexion avec
          l’étranger. Et puis les couches de la population plus pauvres ont été
          davantage touchées. Genève a probablement connu davantage de cas pour
          toutes ces raisons.»
        </p>
        <p>
          En dehors de ces facteurs structurels, la rapidité avec laquelle des
          mesures ont été activées a également une influence importante, selon
          Olivia Keiser. Enfin, il y a certainement aussi une part de hasard: un
          cluster de cas au mauvais endroit peut faire monter la mortalité.
          Surtout dans les cantons plus petits.
        </p>
        <p>
          Pour son collègue le professeur Antoine Flahault, les hommes et les
          femmes politiques peuvent toutefois avoir une influence importante sur
          le cours de l’épidémie: «Au Brésil, en Russie, en Inde, on a vu que
          les populismes ont contribué à aggraver la crise. Dans certains
          cantons, par exemple à Saint-Gall, des prises de position de
          représentants des autorités qui avaient tendance à minimiser la
          gravité de l’épidémie ont pu jouer un rôle.»
        </p>
        <AutofitIFrame
          className='AFI'
          src='https://datawrapper.dwcdn.net/5rAUN/'
        />
        <p>
          Le canton de Vaud, avec une surmortalité de 12%, s’en est plutôt bien
          tiré tandis que le Valais, Neuchâtel et Genève (de 17% à 20%) ont
          davantage connu de morts qu’en moyenne nationale.
        </p>
        <p>
          La surmortalité est un indicateur très fiable, qui permet aussi de
          déduire à quel point le reste de la population a été touché. Et
          notamment les personnes souffrant du syndrome de Covid long, que l’on
          estime jusqu’à dix fois plus nombreuses que le total des morts.
        </p>
      </>
    )
  },
  unemploy: {
    title: <>2. La santé économique</>,
    text: (
      <>
        <p>
          L’économie suisse a extrêmement bien résisté à la pandémie, comme le
          révèle une analyse de notre journal. Et les prévisions du KOF, de
          l’unité de recherche économique de l’EPFZ, sont elles aussi
          optimistes: +4% de Produit intérieur brut (PIB) pour la période
          post-Covid.
        </p>
        <p>
          Cela se reflète également dans l’augmentation limitée des chiffres du
          chômage. Le marché de l’emploi en Appenzell n’a pratiquement senti
          aucun impact de la crise, tandis que Zurich, Bâle et les cantons
          latins ont connu une augmentation entre 0,9 point en Valais et 1,6
          point pour le Jura.
        </p>
        <p>
          «La raison de ces variations est à chercher dans les différentes
          structures économiques des cantons», explique Daniel Kopp, économiste
          au KOF.
        </p>
        <p>
          Les plus grands cantons, tels que Zurich,Vaud et Berne, «sont
          largement diversifiés et sont donc mieux à même d’absorber les
          difficultés économiques des industries fortement touchées», estime
          Daniel Kopp.
        </p>
        <p>
          En revanche, pour le Jura, qui dépend fortement du secteur horloger,
          il a été plus difficile de compenser le ralentissement des
          exportations. Le taux de chômage y est passé de 3,6% à 5,2%.
        </p>
      </>
    )
  },
  kof: {
    title: <>3. Les restrictions sanitaires</>,
    text: (
      <>
        <p>
          Pour mesurer la rigueur des mesures de lutte contre le coronavirus,
          nous avons choisi l’indice KOF Stringency+. Mis au point par des
          scientifiques de l’EPFZ, il montre l’évolution des restrictions
          sanitaires au fil du temps et permet une comparaison entre les
          cantons. Les valeurs vont de 0 (aucune restriction) à 100 (confinement
          total).
        </p>
        <p>
          L’index se compose de onze indicateurs, dont l’obligation de faire du
          home office, la fermeture des écoles ou les restrictions en matière de
          rassemblement.
        </p>
        <p>
          Les cantons se sont comportés de manière très différente en ce qui
          concerne la fermeture de certains secteurs d’activité économiques. «En
          particulier en Suisse romande, qui a été plus durement touchée, les
          cantons ont déjà fermé des restaurants et Genève des magasins au début
          de la deuxième vague, en octobre 2020. «D’un autre côté, certains
          cantons de Suisse alémanique ont procédé à des fermetures volontaires
          début décembre, dans l’espoir de pouvoir rouvrir le secteur
          touristique pour Noël», ajoute Regina Pleninger.
        </p>
        <p>
          L’économiste du KOF examine l’effet des mesures Covid dans une étude
          récente. «Les plus efficaces étaient l’obligation de home office et la
          fermeture des commerces et des restaurants», dit-elle. Mais la
          population a également restreint ces activités sans qu’il n’y ait
          nécessairement eu besoin d’interdictions, dit-elle. Ce changement de
          comportement se voit de façon très nette dans les données de mobilité
          et des transactions par carte de crédit.
        </p>
      </>
    )
  },

  vaccdays: {
    title: <>4. La campagne de vaccination</>,
    text: (
      <>
        <p>
          La Suisse se situe dans la moyenne européenne, avec 38% de la
          population entièrement vaccinée. Cela dit, elle accélère le rythme. Si
          l’on compare la vitesse de la campagne de vaccination dans les
          cantons, Fribourg est en toute fin de peloton, avec seulement 29% de
          la population entièrement vaccinée.
        </p>
        <AutofitIFrame
          className='AFI'
          src='https://datawrapper.dwcdn.net/RemRi/'
        />
        <p>
          Cette statistique est toutefois en défaveur du canton du ministre de
          la Santé Alain Berset. En raison des collaborations hospitalières avec
          Vaud et Berne, Fribourg a vu{' '}
          <Link href='276216556625'>une partie de sa population</Link> se faire
          vacciner chez ses voisins – et finalement être comptabilisée. Mais
          même sans cela, la campagne de vaccination fribourgeoise reste
          beaucoup plus lente que la moyenne.
        </p>
        <p>
          Genève, avec Bâle-Ville et le Tessin, est l’un des cantons à avoir
          vacciné le plus rapidement sa population.
        </p>
        <AutofitIFrame
          className='AFI'
          src='https://datawrapper.dwcdn.net/f6gnV/'
        />
      </>
    )
  },
  tests: {
    title: <>5. La politique de tests</>,
    text: (
      <>
        <p>
          Le canton des Grisons a été le premier à effectuer des tests de masse,
          à partir de fin janvier 2021. Cette politique, inspirée de ce qui a
          été pratiqué en Autriche, l’a propulsé en tête des cantons qui testent
          le plus, avec 143 tests de dépistage Covid-19 par 100 habitants.
        </p>
        <AutofitIFrame
          className='AFI'
          src='https://datawrapper.dwcdn.net/nVzo4/'
        />
        <p>
          Il est suivi par Genève (130), qui a toujours beaucoup testé, Uri
          (124), Bâle-Ville (112), Vaud et Zoug (110). En revanche, les cantons
          comme le Valais (87), Fribourg (77) ou Lucerne (64) ont testé presque
          deux fois moins. Il s’est ensuivi moins de cas asymptomatiques
          détectés, moins de chaînes de transmission interrompues, davantage de
          cas et de morts.
        </p>
      </>
    )
  },
  positivity: {
    title: <>6. Contrôle de l’épidémie</>,
    text: (
      <>
        <p>
          Le taux de positivité est l’un des indicateurs les plus fiables pour
          dire si les autorités ont perdu ou non le contrôle de l’épidémie.
          L’Organisation mondiale de la santé situe d’ailleurs ce seuil à 5%.
          C’est-à-dire que si plus de 5 tests sur 100 sont positifs, c’est le
          signe qu’on ne teste pas assez, qu’on ne parvient plus à identifier
          suffisamment de personnes porteuses du virus. C’est notamment un
          problème avec les cas asymptomatiques.
        </p>
        <p>
          Avec un peu moins de 7% sur l’ensemble de la deuxième vague,
          Bâle-Ville est le canton qui a le taux le plus bas, avec un pic à 16%
          seulement. «Pour arriver à un tel résultat, la réactivité a été
          déterminante», dit Thomas Steffen, son médecin cantonal. Le canton
          offre aujourd’hui 66 lieux ou possibilités différentes de se faire
          tester.
        </p>
        <AutofitIFrame
          className='AFI'
          src='https://datawrapper.dwcdn.net/Mhy3L/'
        />
        <p>
          Bâle-Ville est notamment suivi par les grands cantons avec un hôpital
          universitaire comme Zurich, Berne, Genève et Vaud, tous entre 8% et
          11%.
        </p>
        <p>
          Les cantons de Fribourg, de Saint-Gall, de Schaffhouse, du Jura et du
          Valais sont tous au-dessus de 13%. Le pic a été atteint en novembre
          par le canton de Fribourg, avec un taux de 32%.
        </p>
        <AutofitIFrame
          className='AFI'
          src='https://datawrapper.dwcdn.net/YsETc/'
        />
      </>
    )
  },

  icu: {
    title: <>7. Surcharge des soins intensifs</>,
    text: (
      <>
        <p>
          Ce chiffre montre le nombre de jours où les soins intensifs ont été
          occupés à plus de 90% durant la deuxième vague. La plupart des cantons
          n’ont pas connu cette situation et sont donc tous dans la zone bleue.
          Globalement, dit le professeur Antoine Flahault, le système
          hospitalier suisse n’a jamais été menacé durant la seconde vague.
        </p>
        <p>
          Cela dit, des cantons de taille moyenne comme Soleure (93 jours),
          Fribourg (49 jours) ou le Valais (27 jours) ont franchi ce seuil
          durant de longues semaines.
        </p>
        <p>
          C’est un indicateur robuste, dit Thomas Steffen, médecin cantonal de
          Bâle-Ville. «Lorsque le taux d’occupation de l’unité de soins
          intensifs dépasse 90%, on est clairement à la limite et il faut agir.»
        </p>
      </>
    )
  },
  fazit: {
    title: <>Le bilan</>,
    text: (
      <>
        <p>
          La Suisse s’est comparativement bien sortie de la pandémie, comme l’a
          montré notre analyse publiée par{' '}
          <Link
            href={
              'https://interaktiv.' +
              parseHostname().publicationDomain +
              '/2021/bilan-covid19-pays-suisse/'
            }
            allowWordWrap
          >
            Tamedia il y a un
          </Link>{' '}
          mois.
        </p>
        <p>
          Mais un examen détaillé des cantons révèle aujourd’hui des différences
          majeures: les Grisons est le canton qui s’est le mieux sorti de la
          pandémie, alors même qu’il avait été sermonné à plusieurs reprises
          pendant la crise.
        </p>
        <p>
          On l’a dit, à la mi-janvier 2021, les Grisons sont devenus le premier
          canton à lancer des tests de masse en Suisse afin d’arrêter les
          chaînes d’infection. En contrepartie, les restaurants devaient rouvrir
          plus tôt que dans les autres cantons. Au début, les autorités
          fédérales ont critiqué l’approche grisonne, mais ensuite les tests de
          masse sont devenus la norme.
        </p>
        <p>
          {' '}
          <Link href='361703326871' allowWordWrap>
            Tous les derniers chiffres sur l'épidémie en Suisse.
          </Link>
        </p>
        <p>
          En janvier 2021 toujours, les Grisons se sont fait houspiller dans les
          médias internationaux, accusés de négligence après qu’une douzaine
          d’employés d’hôtels de Saint-Moritz ont été testés positifs au variant
          sud-africain. Et les Grisons ont également suivi leur propre voie avec
          d’autres cantons dans le cadre de la «guerre des terrasses». Contre la
          volonté du Conseil fédéral, les terrasses des restaurants des stations
          de ski sont restées ouvertes, du moins temporairement.
        </p>
        <p>
          Au bout du compte, notre évaluation montre que le chemin particulier
          des Grisons n’était peut-être pas si mauvais.
        </p>
      </>
    )
  },
  method: {
    title: <>D’où viennent ces données?</>,
    text: (
      <>
        <div className='sources'>
          <p>
            <b>Surmortalité</b>
            <span className='source'>
              Source:{' '}
              <Link
                allowWordWrap
                href='https://www.experimental.bfs.admin.ch/expstat/fr/home/methodes-innovation/momo.html'
              >
                OFSP
              </Link>
            </span>
            Les décès de chaque semaine calendaire sont comparés aux décès
            statistiquement attendus pour cette semaine (sur la base des décès
            effectifs des dix dernières années). Pour la comparaison entre
            cantons, la valeur moyenne de la deuxième vague (septembre 2020 à
            mai 2021) a été prise en compte.
          </p>
          <p>
            <b>Taux de chômage</b>
            <span className='source'>
              Source:{' '}
              <Link allowWordWrap href='https://www.amstat.ch/v2/fr/index.html'>
                Secrétariat d’État à l’économie SECO
              </Link>
            </span>
            Le SECO communique le taux de chômage par canton sur une base
            mensuelle. Afin de pouvoir saisir l’influence de la crise du Covid
            sur ce taux, une valeur moyenne par canton a été calculée pour la
            période de juin 2020 à mai 2021 et comparée à la valeur moyenne de
            2018 et 2019. Nous montrons ici la différence.
          </p>
          <p>
            <b>Stringency</b>
            <span className='source'>
              Source:{' '}
              <Link
                allowWordWrap
                href='https://kof.ethz.ch/fr/previsions-indicateurs/indicateurs/kof-stringency-index.html'
              >
                Centre de recherches conjoncturelles de l’EPFZ (KOF)
              </Link>
              L'indice Stringency Plus du Centre de recherches conjoncturelles
              de l'EPFZ (KOF) indique dans quelle mesure les autorités
              cantonales restreignent la vie publique et privée. On y tient
              notamment compte des fermetures des écoles ou du home office
              obligatoires. La valeur moyenne entre juillet 2020 et janvier 2021
              est prise en compte dans cette comparaison. 0 signifie aucune
              restriction et 100 des restrictions maximales. Les valeurs les
              plus basses correspondent aux mesures fédérales, car les cantons
              doivent appliquer les règles nationales. Toutefois, ils peuvent
              également introduire des règles plus strictes.
            </span>
          </p>
          <p>
            <b>Vaccination</b>
            <span className='source'>
              Source:{' '}
              <Link
                allowWordWrap
                href='https://www.covid19.admin.ch/fr/overview'
              >
                OFSP
              </Link>
            </span>
            Pour la comparaison entre les cantons, tous les jours pendant
            lesquels les personnes ont été vaccinées sont additionnés dans
            chaque canton. Il est ainsi possible de calculer le nombre moyen de
            jours pendant lesquels une personne a été vaccinée. Les personnes
            vaccinées deux fois comptent deux fois. Cette valeur donne une
            meilleure image que le nombre actuel de personnes vaccinées, car une
            vaccination précoce des résidents a pu réduire les infections et les
            éventuelles mutations du virus.
            <br />
            <br />
            Le nombre de doses de vaccin administrées par canton est indiqué
            dans le temps. Aucune distinction n’est faite selon qu’une personne
            a reçu deux doses de vaccin ou que deux personnes en ont reçu une
            chacune. Ce chiffre est donc différent du nombre de personnes
            totalement ou simplement vaccinées.
          </p>
          <p>
            <b>Tests</b>
            <span className='source'>
              Source:{' '}
              <Link
                allowWordWrap
                href='https://www.covid19.admin.ch/fr/overview'
              >
                OFSP
              </Link>
            </span>
            Nombre de tests effectués dans un canton. Les tests PCR et les tests
            rapides antigéniques sont comptés. Les auto-tests qui ne sont pas
            enregistrés de manière centralisée ne sont pas pris en compte.
            Jusqu’à la fin du mois de juin 2021.
          </p>

          <p>
            <b>Taux de positivité des tests</b>
            <span className='source'>
              Source:{' '}
              <Link
                allowWordWrap
                href='https://www.covid19.admin.ch/fr/overview'
              >
                OFSP
              </Link>
            </span>
            Positivité moyenne lors de la deuxième vague (septembre 2020 à mai
            2021).
          </p>
          <p>
            <b>Unités de soins intensifs</b>
            <span className='source'>
              Source:{' '}
              <Link
                allowWordWrap
                href='https://www.covid19.admin.ch/fr/overview'
              >
                OFSP
              </Link>
            </span>
            Sont comptés le nombre de jours pendant lesquels les unités de soins
            intensifs du canton concerné ont été utilisées à plus de 90% de leur
            capacité. D’avril 2020 à juin 2021. Ces chiffres doivent être
            interprétés avec prudence, notamment parce que les «places ICU» ont
            été comptabilisées différemment dans les cantons. Certains annoncent
            uniquement les places certifiées, d’autres non.
          </p>
        </div>
      </>
    )
  }
}
